/**
 * recorder — the microphone, and the frames it produces.
 *
 * The only impure part of dictation: getUserMedia, an AudioContext, and a
 * callback per frame. Everything that DECIDES anything (when a piece of speech
 * has ended, how it becomes a WAV) is pure and lives beside this, so the rules
 * are unit-tested and this is a thin edge.
 *
 * WHY A SCRIPT PROCESSOR AND NOT AN AUDIO WORKLET. A worklet needs a separate
 * module file fetched at runtime, which CRA's build would have to be taught to
 * emit and the page's CSP would have to allow. `createScriptProcessor` is
 * deprecated but present and reliable in every browser this product supports,
 * and it is what the platform's own realtime voice page already uses on the
 * same job (preston-realtime.html) — so this is the shape that is proven here,
 * not a new bet. If it is ever removed, the worklet is a contained swap.
 *
 * The three constraints that matter are set on the track, not on us:
 * echo cancellation, noise suppression and automatic gain. They are why a
 * laptop in a room produces something a recognizer can read.
 */

import { DICTATION_SAMPLE_RATE, downsampleTo16k } from './wavEncoder';

/** ~4096 samples is about 85 ms at 48 kHz: fine enough to spot a pause inside
 *  the 700 ms window, coarse enough not to spend the main thread on callbacks. */
const FRAME_SIZE = 4096;

export interface RecorderHandle {
  /** Stop the microphone and release the track. Idempotent. */
  stop: () => void;
  /** The context's real rate, for the record. */
  sampleRate: number;
}

export interface RecorderCallbacks {
  /** One frame of 16 kHz mono samples, already downsampled. */
  onFrame: (samples: Float32Array) => void;
  /** The track ended on its own (unplugged, taken by another app). */
  onLost?: () => void;
}

export type RecorderFailure = 'denied' | 'unavailable' | 'failed';

export interface RecorderStart {
  ok: boolean;
  handle?: RecorderHandle;
  failure?: RecorderFailure;
}

type AudioContextCtor = new (options?: AudioContextOptions) => AudioContext;

function audioContextCtor(): AudioContextCtor | null {
  const w = window as unknown as { AudioContext?: AudioContextCtor; webkitAudioContext?: AudioContextCtor };
  return w.AudioContext || w.webkitAudioContext || null;
}

/**
 * Open the microphone.
 *
 * A refusal is reported as `denied` and NOT as an error: somebody declining the
 * browser's prompt is an ordinary answer, and the surface says how to change
 * their mind rather than showing them a failure.
 */
export async function startRecorder(callbacks: RecorderCallbacks): Promise<RecorderStart> {
  const Ctor = audioContextCtor();
  if (!Ctor || !navigator?.mediaDevices?.getUserMedia) return { ok: false, failure: 'unavailable' };

  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: 1,
      },
    });
  } catch (err) {
    const name = String((err as Error)?.name || '');
    if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError') {
      return { ok: false, failure: 'denied' };
    }
    if (name === 'NotFoundError' || name === 'OverconstrainedError' || name === 'NotReadableError') {
      return { ok: false, failure: 'unavailable' };
    }
    return { ok: false, failure: 'failed' };
  }

  let context: AudioContext;
  try {
    context = new Ctor();
    // Safari hands back a suspended context outside a gesture; the press is a
    // gesture, so this resolves — and a failure here still stops cleanly below.
    if (context.state === 'suspended') await context.resume();
  } catch {
    stream.getTracks().forEach((t) => t.stop());
    return { ok: false, failure: 'failed' };
  }

  const source = context.createMediaStreamSource(stream);
  const processor = context.createScriptProcessor(FRAME_SIZE, 1, 1);
  const rate = context.sampleRate;

  processor.onaudioprocess = (event) => {
    const input = event.inputBuffer.getChannelData(0);
    // Copied: the event's buffer is reused by the graph on the next callback,
    // so keeping a reference would hand the segmenter samples that change.
    callbacks.onFrame(downsampleTo16k(new Float32Array(input), rate));
  };

  source.connect(processor);
  // A ScriptProcessor only runs while it is connected to a destination. A gain
  // of zero keeps it running without playing the microphone back into the room,
  // which would be a feedback loop through the speaker.
  const mute = context.createGain();
  mute.gain.value = 0;
  processor.connect(mute);
  mute.connect(context.destination);

  for (const track of stream.getTracks()) {
    track.addEventListener('ended', () => callbacks.onLost?.(), { once: true });
  }

  let stopped = false;
  const stop = () => {
    if (stopped) return;
    stopped = true;
    try { processor.onaudioprocess = null; } catch { /* already detached */ }
    try { processor.disconnect(); } catch { /* already detached */ }
    try { mute.disconnect(); } catch { /* already detached */ }
    try { source.disconnect(); } catch { /* already detached */ }
    for (const track of stream.getTracks()) {
      try { track.stop(); } catch { /* already stopped */ }
    }
    // Closing the context releases the audio hardware; on a phone, leaving it
    // open is what keeps the recording indicator lit after the user is done.
    void context.close().catch(() => undefined);
  };

  return { ok: true, handle: { stop, sampleRate: rate === DICTATION_SAMPLE_RATE ? rate : rate } };
}
